import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Check, Copy, Inbox, Paperclip, Search, SlidersHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { InboxEmail, InboxFolder } from '@hub/contracts'
import { orpc } from '@/lib/orpc-client'
import { useProjectEvent } from '@/lib/collab/presence-context'
import { useDateLocale } from '@/i18n/dates'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { EmailStatusPill } from './status-pill'
import { EmailDetail } from './email-detail'
import { HubBubble } from '@/components/chat/hub-bubble'
import { ContactQr } from './contact-qr'

const FOLDERS: InboxFolder[] = ['inbox', 'processed', 'archive']

export interface InboxPageProps {
  projectId: string
  folder: InboxFolder
  selectedEmailId: string | null
  onChange: (next: { folder: InboxFolder; email: string | null }) => void
}

type ListResult = Awaited<ReturnType<typeof orpc.inbox.listEmails>>
type AddressResult = Awaited<ReturnType<typeof orpc.inbox.getAddress>>

export function InboxPage({ projectId, folder, selectedEmailId, onChange }: InboxPageProps) {
  const { t } = useTranslation('inbox')
  const { intl } = useDateLocale()
  const [address, setAddress] = useState<AddressResult | null>(null)
  const [copied, setCopied] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [result, setResult] = useState<ListResult | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    orpc.inbox
      .getAddress({ projectId })
      .then((r) => setAddress(r))
      .catch(() => setAddress(null))
  }, [projectId])

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 250)
    return () => clearTimeout(handle)
  }, [query])

  const load = useCallback(async () => {
    try {
      const res = await orpc.inbox.listEmails({
        projectId,
        folder,
        query: debouncedQuery || undefined,
      })
      setResult(res)
    } catch {
      // keep the previous list; the detail pane reports its own errors
    } finally {
      setLoading(false)
    }
  }, [projectId, folder, debouncedQuery])

  useEffect(() => {
    setLoading(true)
    void load()
  }, [load])

  useProjectEvent(['inbox:changed'], () => void load())

  const items = result?.items ?? []
  const selected = useMemo(
    () => items.find((e) => e.id === selectedEmailId) ?? null,
    [items, selectedEmailId]
  )

  const copyAddress = async () => {
    if (!address) return
    try {
      await navigator.clipboard.writeText(address.address)
      setCopied(true)
      toast.success(t('address.copied'))
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard unavailable — the address is still visible to select by hand
    }
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    const sameDay = d.toDateString() === new Date().toDateString()
    return sameDay
      ? d.toLocaleTimeString(intl, { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString(intl, { day: 'numeric', month: 'short' })
  }

  const emptyKey = debouncedQuery ? 'search' : folder

  return (
    <div className="flex h-full min-h-0 flex-1">
      {/* List column */}
      <aside
        className={cn(
          'flex w-full shrink-0 flex-col border-r bg-background md:w-[380px]',
          selectedEmailId && 'hidden md:flex'
        )}
      >
        <div className="space-y-3 border-b px-4 pt-4 pb-3">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-semibold">{t('title')}</h1>
            <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5 px-2">
              <Link to="/$projectId/inbox/rules" params={{ projectId }}>
                <SlidersHorizontal className="h-4 w-4" />
                {t('rulesLink')}
              </Link>
            </Button>
          </div>
          {address && (
            <div className="flex items-center gap-1.5">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => void copyAddress()}
                      className="flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border bg-muted/40 px-2.5 text-left text-xs text-muted-foreground hover:bg-muted"
                    >
                      <span className="truncate font-mono">{address.address}</span>
                      {copied ? (
                        <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-emerald-600" />
                      ) : (
                        <Copy className="ml-auto h-3.5 w-3.5 shrink-0" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="font-medium">{t('address.label')}</p>
                    <p className="text-xs opacity-80">{t('address.hint')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <ContactQr contact={address.contact} />
            </div>
          )}
          <div className="relative">
            <Search className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="h-9 pl-8"
            />
          </div>
          <div className="flex gap-1">
            {FOLDERS.map((f) => {
              const count = result?.counts[f] ?? 0
              const attention = f === 'inbox' ? (result?.attention ?? 0) : 0
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => onChange({ folder: f, email: null })}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                    f === folder
                      ? 'bg-foreground text-background dark:bg-accent dark:text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  {t(`folders.${f}`)}
                  {count > 0 && (
                    <span
                      className={cn(
                        'rounded-full px-1.5 text-[10px] tabular-nums',
                        f === folder
                          ? 'bg-background/20 dark:bg-foreground/15'
                          : 'bg-muted-foreground/15'
                      )}
                    >
                      {attention > 0 ? `${attention}/${count}` : count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && items.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('loading')}</p>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
              <Inbox className="h-8 w-8 opacity-40" />
              {t(`empty.${emptyKey}`)}
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((email) => (
                <EmailRow
                  key={email.id}
                  email={email}
                  active={email.id === selectedEmailId}
                  date={formatDate(email.receivedAt)}
                  onClick={() => onChange({ folder, email: email.id })}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* Detail column */}
      <section className={cn('min-w-0 flex-1', !selectedEmailId && 'hidden md:block')}>
        {selectedEmailId ? (
          <EmailDetail
            key={selectedEmailId}
            projectId={projectId}
            emailId={selectedEmailId}
            summary={selected}
            onBack={() => onChange({ folder, email: null })}
            onMoved={(nextFolder) => onChange({ folder: nextFolder, email: selectedEmailId })}
            onDeleted={() => onChange({ folder, email: null })}
            onChanged={() => void load()}
          />
        ) : (
          <div className="flex h-full items-center justify-center p-10 text-sm text-muted-foreground">
            {t('select')}
          </div>
        )}
      </section>

      {/* PLAN-084: the HUB bubble follows the selected email — its
          chat is the email's own thread (shared with the email's runs).
          Keyed by email so switching mail swaps the thread. */}
      {selectedEmailId && (
        <HubBubble
          key={selectedEmailId}
          projectId={projectId}
          context={{
            kind: 'email',
            emailId: selectedEmailId,
            subject: selected?.subject ?? null,
            threadId: selected?.threadId ?? null,
          }}
        />
      )}
    </div>
  )
}

function EmailRow({
  email,
  active,
  date,
  onClick,
}: {
  email: InboxEmail
  active: boolean
  date: string
  onClick: () => void
}) {
  const { t } = useTranslation('inbox')
  const unread = email.status === 'new'
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-muted/50',
          active && 'bg-muted'
        )}
      >
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              unread ? 'font-semibold' : 'font-medium'
            )}
          >
            {email.fromName || email.fromAddress}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{date}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn('min-w-0 flex-1 truncate text-sm', unread && 'font-medium')}>
            {email.subject || t('noSubject')}
          </span>
          {email.hasAttachments && (
            <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {email.snippet}
          </span>
          <EmailStatusPill status={email.status} />
        </div>
      </button>
    </li>
  )
}
